import {X} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {COL_SPAN_CLASS, resolveFieldLabel} from '@/types/form-layout'
import type {LayoutSchema, Palette} from '@/types/form-layout'

// ============================================================================
// Stub FormLayoutRenderer
// ============================================================================

function FormLayoutRendererStub({schema, palette}: {schema: LayoutSchema; palette: Palette | null}) {
    const i18n = useI18n()

    return (
        <div className="space-y-6 p-4">
            {schema.sections.map((section) => (
                <div key={section.key} className="rounded-lg border p-4">
                    <h3 className="mb-3 font-semibold" data-testid={`preview-section-title-${section.key}`}>{section.label}</h3>
                    <div className="grid grid-cols-12 gap-3">
                        {section.fields
                            .filter((f) => !f.hidden)
                            .map((field, idx) => {
                                const label = resolveFieldLabel(field, palette)
                                const defaultVal = field.defaultValue ?? ''
                                const defaultSpan = Math.floor(12 / (section.columns ?? 1))
                                const colSpan = Math.min(Math.max(field.colSpan ?? defaultSpan, 1), 12)

                                return (
                                    <div
                                        key={idx}
                                        className={`space-y-1 ${COL_SPAN_CLASS[colSpan]}`}
                                    >
                                        <label className="text-sm font-medium">{label}</label>
                                        <input
                                            type="text"
                                            defaultValue={defaultVal}
                                            disabled={field.disabled}
                                            placeholder={field.placeholder ?? ''}
                                            className="w-full rounded border px-3 py-1.5 text-sm disabled:opacity-50"
                                        />
                                    </div>
                                )
                            })}
                    </div>
                </div>
            ))}
            {schema.sections.length === 0 && (
                <p className="text-center text-muted-foreground">{i18n.formLayouts.previewEmpty}</p>
            )}
        </div>
    )
}

// ============================================================================
// PreviewPane
// ============================================================================

interface PreviewPaneProps {
    schema: LayoutSchema
    useCaseKey: string
    palette: Palette | null
    onExitPreview: () => void
}

export function PreviewPane({schema, palette, onExitPreview}: PreviewPaneProps) {
    const i18n = useI18n()

    return (
        <div className="flex flex-1 flex-col overflow-hidden">
            {/* Amber banner */}
            <div className="flex items-center gap-4 bg-amber-50 border-b border-amber-200 px-4 py-2 dark:bg-amber-950/30 dark:border-amber-800">
                <p className="flex-1 text-sm text-amber-800 dark:text-amber-200 font-medium">
                    {i18n.formLayouts.previewBanner}
                </p>

                {/* Exit preview */}
                <Button data-testid="exit-preview-btn" variant="outline" size="sm" onClick={onExitPreview}>
                    <X className="mr-1.5 h-4 w-4" />
                    {i18n.formLayouts.exitPreview}
                </Button>
            </div>

            {/* Preview content */}
            <div className="flex-1 overflow-y-auto">
                <div className="mx-auto max-w-2xl">
                    <FormLayoutRendererStub schema={schema} palette={palette} />
                </div>
            </div>
        </div>
    )
}
